import { observer, use$ } from '@legendapp/state/react';
import { SAMPLE, C, depthMeta } from '../data/sample';
import type { App, NicheFunction } from '../data/sample';
import { state$ } from '../state';

type ProvKey = 'pricing' | 'docs' | 'listing' | 'review' | 'description';
type SupKey = 'covered' | 'q+' | 'q-' | 'request';

const PROV: Record<ProvKey, { l: string; bg: string; fg: string }> = {
  pricing: { l: 'Pricing', bg: '#303030', fg: '#fff' },
  docs: { l: 'Docs', bg: '#303030', fg: '#fff' },
  listing: { l: 'Listing', bg: '#eef0f2', fg: '#303030' },
  review: { l: 'Review', bg: '#e3f1df', fg: '#008060' },
  description: { l: 'Description', bg: '#f1f3f5', fg: '#6d7175' },
};
const SUP: Record<SupKey, { l: string; fg: string; dot: string }> = {
  covered: { l: 'Confirmed', fg: '#616161', dot: '●' },
  'q+': { l: 'Plus', fg: '#008060', dot: '▲' },
  'q-': { l: 'Minus', fg: '#d72c0d', dot: '▼' },
  request: { l: 'Unmet demand', fg: '#b98900', dot: '◇' },
};

interface EvItem {
  provLabel: string;
  provBg: string;
  provFg: string;
  supLabel: string;
  supFg: string;
  supDot: string;
  text: string;
}

function evidenceFor(a: App, f: NicheFunction, depth: number, gated: boolean): EvItem[] {
  const fl = f.name.toLowerCase();
  const items: { src: ProvKey; sp: SupKey; text: string }[] = [];
  if (depth === 0) {
    items.push({
      src: 'listing',
      sp: 'covered',
      text: `Listing & description for ${a.name} don’t mention “${f.name}”.`,
    });
    items.push({
      src: 'review',
      sp: 'request',
      text: `“Too bad ${a.name} doesn’t have ${fl} yet — had to install another app for this.” — 4★ review`,
    });
  } else {
    items.push({
      src: depth >= 2 ? 'docs' : 'listing',
      sp: 'covered',
      text:
        depth >= 2
          ? `The docs for ${a.name} describe the ${fl} flow in detail, with config steps.`
          : `The listing lists “${f.name}” among its main features.`,
    });
    if (gated)
      items.push({
        src: 'pricing',
        sp: 'covered',
        text: `“${f.name}” only unlocks on a paid plan of ${a.name} — evidence this function is “worth paying for”.`,
      });
    if (depth >= 2)
      items.push({
        src: 'review',
        sp: 'q+',
        text: `“${f.name} from ${a.name} runs smoothly, exactly what my store needs.” — 5★ review`,
      });
    if (depth <= 2)
      items.push({
        src: 'review',
        sp: 'q-',
        text: `“${f.name} still lacks customization, hope ${a.name} improves it.” — 3★ review`,
      });
  }
  return items.map((it) => {
    const p = PROV[it.src];
    const su = SUP[it.sp];
    return {
      provLabel: p.l,
      provBg: p.bg,
      provFg: p.fg,
      supLabel: su.l,
      supFg: su.fg,
      supDot: su.dot,
      text: it.text,
    };
  });
}

export const EvidenceDrawer = observer(function EvidenceDrawer() {
  const drawer = use$(state$.drawer);
  if (!drawer) return null;

  const d = SAMPLE;
  const a = d.apps.find((x) => x.id === drawer.appId);
  const f = d.functions.find((x) => x.id === drawer.funcId);
  if (!a || !f) return null;

  const fi = d.functions.indexOf(f);
  const depth = a.depths[fi]!;
  const sc = a.scores[fi];
  const gated = a.gated[fi]!;
  const dm = depthMeta(depth);

  let summary: string;
  if (depth === 0)
    summary = `${a.name} doesn’t do “${f.name}”. Not found in the listing, docs, or pricing — but there is demand in reviews.`;
  else if (depth === 3)
    summary = `${a.name} covers “${f.name}” very deeply: praised in reviews and used as a selling point.`;
  else if (depth === 2)
    summary = `${a.name} does “${f.name}” well, but there is still a customization gap vs. demand.`;
  else
    summary = `${a.name} only does “${f.name}” at a basic level — easy to outdo by going deeper.`;

  const ev = evidenceFor(a, f, depth, gated);
  const close = () => state$.drawer.set(null);

  return (
    <>
      <div
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 60,
          background: 'rgba(20,22,28,0.34)',
          animation: 'nl-fade .2s ease',
        }}
      />
      <aside
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 61,
          width: 440,
          maxWidth: '92vw',
          background: '#fff',
          boxShadow: '-12px 0 40px rgba(20,22,28,0.18)',
          animation: 'nl-drawer .26s cubic-bezier(.2,.8,.2,1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: '20px 22px 16px', borderBottom: '1px solid #ebebeb' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 11.5,
                color: '#6d7175',
                fontWeight: 600,
              }}
            >
              <span style={{ fontFamily: "'JetBrains Mono'" }}>{a.name}</span>
              <span>›</span>
              <span style={{ color: '#303030' }}>{f.name}</span>
            </div>
            <button
              onClick={close}
              style={{
                background: '#f1f1f1',
                border: 'none',
                width: 30,
                height: 30,
                borderRadius: 8,
                fontSize: 17,
                color: '#616161',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              ×
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 24 }}>
              <span style={{ width: 7, height: 9, borderRadius: 2, background: dm.bars[0] }} />
              <span style={{ width: 7, height: 16, borderRadius: 2, background: dm.bars[1] }} />
              <span style={{ width: 7, height: 24, borderRadius: 2, background: dm.bars[2] }} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontSize: 26,
                    fontWeight: 700,
                    color: sc === null ? '#c2c6cf' : depth >= 2 ? C.primary : C.ink2,
                    lineHeight: 1,
                  }}
                >
                  {sc === null ? '—' : sc}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#303030' }}>{dm.label}</span>
              </div>
              <div style={{ fontSize: 11, color: '#a5abb5', marginTop: 2 }}>
                quality score / depth
              </div>
            </div>
          </div>
          <p
            style={{
              margin: '14px 0 0',
              fontSize: 13,
              color: '#616161',
              lineHeight: 1.55,
              background: '#f6f6f7',
              border: '1px solid #eef0f2',
              borderRadius: 10,
              padding: '11px 13px',
            }}
          >
            {summary}
          </p>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px 30px' }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#6d7175',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 14,
            }}
          >
            Evidence ({ev.length})
          </div>
          {ev.map((e, i) => (
            <div
              key={i}
              style={{
                border: '1px solid #ebebeb',
                borderRadius: 12,
                padding: '13px 14px',
                marginBottom: 11,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9 }}>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono'",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 7px',
                    borderRadius: 6,
                    background: e.provBg,
                    color: e.provFg,
                  }}
                >
                  {e.provLabel}
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: e.supFg,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {e.supDot} {e.supLabel}
                </span>
                <a
                  href="#"
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    color: '#b4b9c2',
                    textDecoration: 'none',
                  }}
                >
                  source ↗
                </a>
              </div>
              <p style={{ margin: 0, fontSize: 12.5, color: '#303030', lineHeight: 1.55 }}>
                {e.text}
              </p>
            </div>
          ))}
          <div
            style={{
              marginTop: 14,
              padding: 13,
              borderRadius: 11,
              background: '#fafbfb',
              border: '1px dashed #d9d9d9',
              fontSize: 11.5,
              color: '#6d7175',
              lineHeight: 1.5,
            }}
          >
            Provenance: confidence <b>Pricing › Docs › Listing › Description</b>. Every score traces
            back to these sources — no numbers “out of thin air”.
          </div>
        </div>
      </aside>
    </>
  );
});
